<template>
  <div class="chart-container">
   
  <canvas ref="chartRef" height="400"></canvas>
   
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent, defineProps } from "vue";
import { Chart } from "chart.js/auto";
import dayjs from "dayjs";

export default defineComponent({
  name: "SalesChart",
});
</script>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  data: {
    type: Array as PropType<any[]>,
    required: true,
  },
  period: {
    type: String,
    required: true,
  },
});

const chartRef = ref<HTMLCanvasElement | null>(null);
let chartInstance: any = null;

const createChart = async () => {
  await nextTick();
  if (!props.data || props.data.length === 0 || !chartRef.value) return;

  if (chartInstance) {
    chartInstance.destroy();
  }

  const ctx = chartRef.value.getContext("2d");
  if (!ctx) return;

  const sortedData = [...props.data].sort((a, b) => {
    const dateA = new Date(a.year, a.month - 1, a.day || 1);
    const dateB = new Date(b.year, b.month - 1, b.day || 1);
    return dateA.getTime() - dateB.getTime();
  });

  const labels = sortedData.map((item) => {
    if (props.period === "daily") {
      return dayjs(`${item.year}-${item.month}-${item.day}`).format("DD-MM-YYYY");
    } else if (props.period === "monthly") {
      return dayjs(`${item.year}-${item.month}-01`).format("MM-YYYY");
    } else {
      return item.year.toString();
    }
  });

  const revenueData = sortedData.map((item) => item.revenue);

  chartInstance = new Chart(ctx, {
    type: "line",
    data: {
      labels: labels,
      datasets: [
        {
          label: t("Pos.CA"),
          data: revenueData,
          borderColor: "#fc8019",
          backgroundColor: "rgba(252, 128, 25, 0.1)",
          tension: 0.1,
          fill: true,
          borderWidth: 3,
          pointRadius: 5,
          pointBackgroundColor: "#fc8019",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return `${t("Pos.CA")}: ${context.parsed.y.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          ticks: {
            callback: function (value: any) {
              return value;
            },
          },
          grid: {
            color: "#eee",
          },
        },
        x: {
          grid: {
            color: "#eee",
          },
        },
      },
    },
  });
};

watch(
  () => [props.data, props.period],
  () => {
    createChart();
  },
  { deep: true }
);

onMounted(createChart);
</script>

<style scoped>
.chart-container {
  height: 500px !important;
  width: 100%;
}
</style>
